import React, { useState } from 'react'
import {
	useMutation,
	useQuery,
	useQueryClient,
} from '@tanstack/react-query'
import {
	LoanComparisonScenario,
	type RunLOCalculationRequest,
} from '@matech/thebigpos-sdk'
import { AxiosError } from 'axios'
import { useIntl } from 'react-intl'
import { LoanCalculatorFormValues } from '@/pages/protected/loanCalculator/types'
import LoanComparisonScenarioTable from './LoanComparisonScenarioTable'
import { getErrorMessage } from '../../../../services/helper'
import { useMixpanel } from '../../../../hooks/useMixpanel'
import { eventTypes } from '../../../../services/constants'
import { TheBigPOSApi } from '../../../../lib/TheBigPOSClient'
import LoanComparisonScenarioForm from '../forms/LoanComparisonScenarioForm'
import { useLoanContext } from '../../../../contexts/LoanContext'
import ErrorList, {
	ErrorListProps,
} from '../../../../components/ErrorList'
import { LayoutRow } from '../../../../components/layout'
import queryKeys from '../../../../services/queryKeys'
import { useAlert } from '../../../../hooks'

const LoanComparisonTemplate = () => {
	const { formatMessage } = useIntl()
	const mixpanel = useMixpanel()
	const queryClient = useQueryClient()
	const { loan } = useLoanContext()
	const loanId = loan?.loanID || ''
	const { alert } = useAlert()

	const [apiErrors, setApiErrors] = useState<
		ErrorListProps['errors'] | null
	>(null)

	const {
		error: loanCalculatorError,
		data: loanCalculatorResult,
		isFetching: loanCalculatorIsFetching,
	} = useQuery({
		queryKey: [queryKeys.loanCalculator, { loanId }],
		queryFn: () => TheBigPOSApi.getLoanCalculator(loanId as string),
		enabled: !!loanId && loanId !== '',
	})

	const loanCalculatorResultData = loanCalculatorResult?.data

	const {
		error: loanComparisonError,
		data: loanComparisonResult,
		isFetching: loanComparisonIsFetching,
	} = useQuery({
		queryKey: [queryKeys.loanComparison, { loanId }],
		queryFn: () => TheBigPOSApi.getLoanComparisons(loanId as string),
		enabled: !!loanId && loanId !== '',
	})

	const loanComparisonResultData = loanComparisonResult?.data

	const invalidateLoanComparisonQueries = async () => {
		await queryClient.invalidateQueries({
			queryKey: [queryKeys.loanComparison],
		})
	}

	const invalidateLoanCalculatorQueries = async () => {
		await queryClient.invalidateQueries({
			queryKey: [queryKeys.loanCalculator],
		})
	}

	const runLoanCalculatorMutation = useMutation({
		mutationFn: (data: RunLOCalculationRequest) =>
			TheBigPOSApi.runLoanCalculator(loanId as string, data),
	})

	const createLoanComparisonMutation = useMutation({
		mutationFn: ({
			index,
			data,
		}: {
			index: number
			data: LoanComparisonScenario
		}) => TheBigPOSApi.createLoanComparison(loanId, index, data),
	})

	const deleteLoanComparisonMutation = useMutation({
		mutationFn: (index: number) =>
			TheBigPOSApi.deleteLoanComparison(loanId, index),
	})

	const handleLoanCalculatorSubmit = async (
		data: LoanCalculatorFormValues
	) => {
		try {
			await runLoanCalculatorMutation.mutateAsync(data)
			mixpanel.trackEvent(eventTypes.CALCULATE_LOAN_PAYMENT, {
				loanId,
			})
			await invalidateLoanCalculatorQueries()
		} catch (e) {
			if (e instanceof AxiosError) {
				if (e.response && e.response.status === 422) {
					setApiErrors(e.response.data.errors)
				}
			}
		}
	}

	const handleCopyLoanToScenario = async ({
		index,
		data,
	}: {
		index: number
		data: LoanCalculatorFormValues
	}) => {
		try {
			const loanCalculatorResponse =
				await runLoanCalculatorMutation.mutateAsync(data)

			const loanCalculatorResponseData = loanCalculatorResponse.data

			await createLoanComparisonMutation.mutateAsync({
				index,
				data: loanCalculatorResponseData as LoanComparisonScenario,
			})
			mixpanel.trackEvent(eventTypes.SAVE_TO_S2_S3, {
				loanId,
			})
			await invalidateLoanComparisonQueries()
			await invalidateLoanCalculatorQueries()
		} catch (e) {
			alert(getErrorMessage(e))
		}
	}

	const handleDeleteScenario = async (index: number) => {
		try {
			await deleteLoanComparisonMutation.mutateAsync(index)
			await invalidateLoanComparisonQueries()
		} catch (e) {
			alert(getErrorMessage(e))
		}
	}

	const loanLocked =
		loanCalculatorResultData?.loanLocked || loan?.isLocked

	if (loanCalculatorError) {
		alert(formatMessage({ id: 'errors.error_loading' }))
	}

	if (loanComparisonError) {
		alert(formatMessage({ id: 'errors.general' }))
	}

	const loading =
		loanCalculatorIsFetching ||
		runLoanCalculatorMutation.isPending ||
		loanComparisonIsFetching ||
		createLoanComparisonMutation.isPending ||
		deleteLoanComparisonMutation.isPending
	return (
		<div className="flex flex-row flex-wrap">
			<div className="md:w-1/3 md:pr-10 w-full pr-0 mb-5 md:mb-0">
				<p className="text-lg font-rubik font-bold dark:text-white">
					Scenario 1
				</p>
				{apiErrors && (
					<LayoutRow>
						<ErrorList errors={apiErrors} />
					</LayoutRow>
				)}
				{loanId && (
					<LoanComparisonScenarioForm
						loanId={loanId}
						loanLocked={loanLocked}
						loading={loading}
						defaultValues={
							loanCalculatorResultData as RunLOCalculationRequest
						}
						onSubmit={handleLoanCalculatorSubmit}
						onCopyLoanToScenario={handleCopyLoanToScenario}
					/>
				)}
			</div>

			{loanCalculatorResultData &&
				loanComparisonResultData?.scenarios?.length &&
				loanComparisonResultData.scenarios.map((scenario, index) => (
					<div
						key={`loan-comparison-${loanId}-${index}`}
						className="md:w-1/3 md:pr-10 w-full pr-0 mb-5 md:mb-0"
					>
						<LoanComparisonScenarioTable
							index={index}
							scenario={scenario}
							loanData={loanCalculatorResultData}
							loading={loading}
							onDelete={handleDeleteScenario}
						/>
					</div>
				))}
		</div>
	)
}

export default LoanComparisonTemplate
